<template>
    <kgr-form-scaffold
        :pending="pending"
        with-back-button
        :save-button-disabled="saveButtonDisabled"
        with-primary-buttons
        @primaryBtnClicked="save"
        @backBtnClicked="redirectToOverview"
    >
        <kgr-loader v-if="loading" />
        <div v-else>
            <div class="kgr-grid kgr-grid--1of2 kgr-grid--gutters kgr-grid--bigGutters">
                <div class="kgr-grid__cell">
                    <kgr-form-row
                        :label="$t('club.exam.title')"
                        :error="error"
                        field-name="title"
                        required
                    >
                        <input
                            v-model.trim="clubExam.title"
                            type="text"
                            class="kgr-input"
                        />
                    </kgr-form-row>
                </div>
                <div class="kgr-grid__cell">
                    <kgr-form-row
                        :label="$t('club.exam.type')"
                        :error="error"
                        field-name="clubExamType"
                    >
                        <kgr-club-exam-type-select
                            v-model="clubExam.clubExamType"
                            :club-id="clubId"
                        />
                    </kgr-form-row>
                </div>
                <div class="kgr-grid__cell">
                    <kgr-form-row
                        :label="$t('club.exam.sport')"
                        :error="error"
                        field-name="sport"
                    >
                        <kgr-sports-select
                            v-model="clubExam.sport"
                            :club-id="clubId"
                            hide-label
                            display="name"
                        />
                    </kgr-form-row>
                </div>
                <div v-if="hasAccessToDogModule" class="kgr-grid__cell">
                    <kgr-form-row
                        :label="$t('Prüflingstyp')"
                        :error="error"
                        field-name="examineeType"
                    >
                        <kgr-generic-select
                            v-model="clubExam.examineeType"
                            :options="examineeTypes"
                            :disabled="clubExam.id !== null"
                            hide-label
                            display="name"
                        />
                    </kgr-form-row>
                </div>
            </div>

            <hr class="kgr-separator kgr-separator--light" />

            <div class="kgr-grid__cell">
                <div class="kgr-headline kgr-headline--h3 kgr-headline--text">
                    {{ $t('Prüfungsinhalte') }}
                </div>
                <table class="kgr-table kgr-table--striped kgr-table--block table-groups kgr-row">
                    <thead>
                        <tr class="kgr-table__row">
                            <th class="kgr-table__cell kgr-table__cell--headline">
                                {{ $t('Beschreibung') }}
                            </th>
                            <th class="kgr-table__cell kgr-table__cell--headline"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(examPart, index) in clubExam.clubExamParts"
                            :key="index"
                            class="kgr-table__row"
                        >
                            <td
                                v-if="isMarkedEditedPart(examPart)"
                                class="kgr-table__cell description"
                            >
                                <div class="kgr-inputGroup kgr-postfix kgr-postfix--top">
                                    <kgr-form-row
                                        :error="error"
                                        :field-name="`clubExamParts[${index}][description]`"
                                        style="width: 100%;"
                                    >
                                        <input
                                            v-model="examPart.description"
                                            type="text"
                                            class="kgr-input"
                                            placeholder="Titel"
                                        />
                                    </kgr-form-row>
                                </div>
                            </td>
                            <td v-else class="kgr-table__cell description">
                                <span :class="examPart.archived ? 'kgr-color kgr-color--gray' : ''">
                                    {{ examPart.description }}
                                </span>
                            </td>
                            <td
                                class="kgr-table__cell"
                                style="vertical-align: top;"
                            >
                                <button
                                    v-if="examPart.id"
                                    class="kgr-table__action"
                                    :title="$t('Bearbeiten')"
                                    @click="markEditedPart(examPart)"
                                >
                                    <kgr-icon-edit size="15" style="pointer-events: none" />
                                </button>
                                <button
                                    :disabled="!examPart.isDeletable"
                                    class="kgr-table__action"
                                    :title="examPart.isDeletable ? $t('Löschen') : null"
                                    @click="examPart.id ? deletePart(examPart.id) : removeNewPart(examPart)"
                                >
                                    <kgr-icon-delete
                                        :class="{ 'kgr-icon--light noPointer': !examPart.isDeletable }"
                                    />
                                </button>
                                <button
                                    v-if="examPart.id"
                                    :disabled="examPart.archived"
                                    class="kgr-table__action"
                                    :title="!examPart.archived ? $t('Prüfungsinhalt archivieren') : null"
                                    @click="archivePart(examPart.id, true)"
                                >
                                    <kgr-icon
                                        icon-class="archive"
                                        :class="{ 'kgr-icon--light noPointer': examPart.archived }"
                                    />
                                </button>
                                <button
                                    v-if="examPart.id"
                                    :disabled="!examPart.archived"
                                    class="kgr-table__action"
                                    :title="examPart.archived ? $t('Prüfungsinhalt reaktivieren') : null"
                                    @click="archivePart(examPart.id, false)"
                                >
                                    <kgr-icon
                                        icon-class="unarchive"
                                        :class="{ 'kgr-icon--light noPointer': !examPart.archived }"
                                    />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div class="kgr-text kgr-text--rightAligned">
                    <div
                        class="kgr-button kgr-button--accent"
                        @click="addPart"
                    >
                        {{ $t('Prüfungsinhalt hinzufügen') }}
                    </div>
                </div>
            </div>
        </div>
    </kgr-form-scaffold>
</template>

<script>
import ClubExam from '../../../js/dto/club/ClubExam';
import ClubExamPart from '../../../js/dto/club/ClubExamPart';
import ClubExamPartRepository from '../../../js/repository/club/ClubExamPartRepository';
import ClubExamRepository from '../../../js/repository/club/ClubExamRepository';
import ClubExamTypeSelect from '../../../components/common/form/select/ClubExamTypeSelect.vue';
import Error from '../../../js/model/Error';
import FormRow from '../../../components/common/form/FormRow.vue';
import FormScaffold from '../../../components/common/form/FormScaffold.vue';
import GenericSelect from '../../../components/common/form/select/GenericSelect.vue';
import Icon from '../../../components/common/icon/Icon.vue';
import IconDelete from '../../../components/common/icon/IconDelete.vue';
import IconEdit from '../../../components/common/icon/IconEdit.vue';
import Loading from '../../../components/common/Loading.vue';
import SportsSelect from '../../../components/common/form/select/SportsSelect.vue';

export default {
    components: {
        'kgr-club-exam-type-select': ClubExamTypeSelect,
        'kgr-form-row': FormRow,
        'kgr-form-scaffold': FormScaffold,
        'kgr-generic-select': GenericSelect,
        'kgr-icon': Icon,
        'kgr-icon-delete': IconDelete,
        'kgr-icon-edit': IconEdit,
        'kgr-loader': Loading,
        'kgr-sports-select': SportsSelect,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        clubExamId: {
            type: String,
            required: false,
            default: null,
        },
        hasAccessToDogModule: {
            type: Boolean,
            required: false,
            default: false,
        },
        editPath: {
            type: String,
            required: true,
        },
        overviewUrl: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            pending: false,
            loading: false,
            clubExam: new ClubExam(),
            editedParts: [],
            examineeTypes: [
                { name: this.$t('Mitglied'), value: ClubExam.EXAMINEE_TYPE_MEMBER },
                { name: this.$t('Hund'), value: ClubExam.EXAMINEE_TYPE_DOG },
            ],
        };
    },
    computed: {
        saveButtonDisabled() {
            return this.pending;
        },
    },
    created() {
        if (this.clubExamId !== null) {
            this.clubExam.id = this.clubExamId;
            this.load(true);
        }
    },
    methods: {
        redirectToOverview() {
            window.location = this.overviewUrl;
        },
        save() {
            if (this.clubExam.id === null) {
                this.create();
            } else {
                this.update();
            }
        },
        create() {
            this.pending = true;
            if (!this.hasAccessToDogModule) {
                this.clubExam.examineeType = { value: ClubExam.EXAMINEE_TYPE_MEMBER };
            }
            ClubExamRepository.create(this.clubId, this.clubExam)
                .then((clubExamId) => {
                    this.clubExam.id = clubExamId;
                    this.updateParts(true);
                })
                .catch((error) => {
                    this.error = error;
                    this.$notify.error('saveChanges.error');
                    this.pending = false;
                });
        },
        update() {
            this.pending = true;
            ClubExamRepository.update(this.clubExam)
                .then(() => this.updateParts(false))
                .catch((error) => {
                    this.error = error;
                    this.$notify.error('saveChanges.error');
                    this.pending = false;
                });
        },
        updateParts(isNewExam) {
            if (isNewExam) {
                this.clubExam.clubExamParts.forEach((examPart) => examPart.clubExamId = this.clubExam.id);
            }
            ClubExamPartRepository.update(this.clubExam.id, this.clubExam.clubExamParts)
                .then(() => {
                    this.load(false);
                    if (isNewExam) {
                        this.$notify.success('Die Prüfung wurde erstellt.');
                        window.location.href = this.editPath + this.clubExam.id;
                    } else {
                        this.$notify.success('saveChanges.success');
                    }
                    this.error = new Error();
                })
                .catch((error) => {
                    this.error = error;
                    this.$notify.error('saveChanges.error');
                })
                .finally(() => this.pending = false);
        },
        load(setLoading) {
            this.loading = setLoading;
            ClubExamRepository.findByClubExamId(this.clubExam.id)
                .then((response) => {
                    this.clubExam = response;
                    if (this.clubExam.examineeType) {
                        this.clubExam.examineeType = this.examineeTypes.filter((examineeType) => examineeType.value === this.clubExam.examineeType)[0];
                    }
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false);
        },
        archivePart(examPartId, archive) {
            this.pending = true;
            ClubExamPartRepository.archive(examPartId, archive)
                .then(() => this.load(false))
                .catch(() => this.$notify.error('saveChanges.error'))
                .finally(() => this.pending = false);
        },
        deletePart(examPartId) {
            showConfirm(this.$t('confirmInformal'), this.$t('Soll der Prüfungsinhalt wirklich gelöscht werden?'), () => {
                this.pending = true;
                ClubExamPartRepository.delete(examPartId)
                    .then(() => {
                        this.load(false);
                        this.$notify.success('Der Prüfungsinhalt wurde gelöscht.');
                    })
                    .catch(() => this.$notify.error('saveChanges.error'))
                    .finally(() => this.pending = false);
            });
        },
        removeNewPart(clubExamPart) {
            this.clubExam.clubExamParts = this.clubExam.clubExamParts.filter((element) => element !== clubExamPart);
        },
        addPart() {
            if (this.loading || this.pending) {
                return;
            }
            const clubExamPart = new ClubExamPart();
            clubExamPart.clubExamId = this.clubExamId;
            this.clubExam.clubExamParts.push(clubExamPart);
            this.markEditedPart(clubExamPart);
        },
        markEditedPart(examPart) {
            this.editedParts.push(examPart);
        },
        isMarkedEditedPart(examPart) {
            return this.editedParts.indexOf(examPart) !== -1;
        },
    },
};
</script>

<style scoped lang="scss">
.noPointer {
    pointer-events: none;
}
.description {
    width: 80%;
}
</style>
